(() => {
  const UMAMI_ENDPOINT = 'https://stats.syrettconsultancy.com/api/send';
  const WEBSITE_ID = '213dddb0-6338-4b18-958e-45fddbf520bb';
  const MAX_ITEMS = 3;
  const MAX_PROPERTIES = 50;
  const VARIANT_BIND_RETRIES = 20;
  const VARIANT_BIND_RETRY_MS = 100;

  const normalizeValue = (value) => {
    if (value === undefined || value === null || value === '') return undefined;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return Number.isFinite(value) ? Number(value.toFixed(4)) : undefined;

    const text = String(value).trim();
    if (!text) return undefined;
    return text.length > 500 ? text.slice(0, 500) : text;
  };

  const normalizeData = (data = {}) => {
    const normalized = {};

    Object.entries(data).some(([key, value]) => {
      if (Object.keys(normalized).length >= MAX_PROPERTIES) return true;
      const normalizedValue = normalizeValue(value);
      if (normalizedValue !== undefined) normalized[key] = normalizedValue;
      return false;
    });

    return normalized;
  };

  const track = (name, data = {}) => {
    try {
      const body = JSON.stringify({
        type: 'event',
        payload: {
          website: WEBSITE_ID,
          hostname: window.location.hostname,
          url: window.location.href,
          referrer: document.referrer,
          name,
          data: normalizeData({ page_path: window.location.pathname, ...data }),
        },
      });

      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        if (navigator.sendBeacon(UMAMI_ENDPOINT, blob)) return;
      }

      fetch(UMAMI_ENDPOINT, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body,
      }).catch(() => {});
    } catch (_error) {
      // Analytics should never block a storefront action.
    }
  };

  const centsToDecimal = (cents) => {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return undefined;
    return amount / 100;
  };

  const appendItemData = (data, item, prefix) => {
    if (!item) return;

    data[`${prefix}_variant_id`] = item.variant_id || item.id;
    data[`${prefix}_product_id`] = item.product_id;
    data[`${prefix}_product_title`] = item.product_title || item.title;
    data[`${prefix}_variant_title`] = item.variant_title;
    data[`${prefix}_handle`] = item.handle;
    data[`${prefix}_sku`] = item.sku;
    data[`${prefix}_quantity`] = item.quantity;
    data[`${prefix}_price`] = centsToDecimal(item.final_price ?? item.price);
  };

  const getItemsFromCartAddResponse = (response) => {
    if (!response) return [];
    if (Array.isArray(response.items)) return response.items;
    return [response];
  };

  const trackAddToBasket = (response, context = {}) => {
    const items = getItemsFromCartAddResponse(response);
    const data = {
      source: context.source || 'product-form',
      selected_variant_id: context.selectedVariantId,
      redirects_to_cart: context.redirectToCart,
      redirects_to_checkout: context.redirectToCheckout,
      added_item_count: items.length,
    };

    items.slice(0, MAX_ITEMS).forEach((item, index) => {
      appendItemData(data, item, items.length === 1 ? 'item' : `item_${index + 1}`);
    });

    track('add_to_basket', data);
  };

  const trackAddToBasketError = (context = {}) => {
    track('add_to_basket_error', {
      source: context.source || 'product-form',
      selected_variant_id: context.selectedVariantId,
      error_message: context.response?.description || context.response?.message,
    });
  };

  const trackCartQuantityChange = (context = {}) => {
    const quantity = Number(context.quantity);
    const previousQuantity = Number(context.previousQuantity);
    const action = quantity === 0 ? 'remove' : quantity > previousQuantity ? 'increase' : 'decrease';

    track(action === 'remove' ? 'cart_item_removed' : 'cart_quantity_changed', {
      source: context.source || 'cart',
      action,
      line: context.line,
      variant_id: context.variantId,
      product_title: context.productTitle,
      previous_quantity: Number.isFinite(previousQuantity) ? previousQuantity : undefined,
      new_quantity: Number.isFinite(quantity) ? quantity : undefined,
      cart_item_count: context.cartData?.item_count,
      cart_total: centsToDecimal(context.cartData?.total_price),
    });
  };

  const trackInitialProductView = () => {
    const product = document.querySelector('[data-umami-product="true"]');
    if (!product) return;

    track('product_viewed', {
      product_id: product.dataset.productId,
      product_title: product.dataset.productTitle,
      product_handle: product.dataset.productHandle,
      product_type: product.dataset.productType,
      variant_id: product.dataset.variantId,
      variant_title: product.dataset.variantTitle,
      price: centsToDecimal(product.dataset.priceCents),
      currency: product.dataset.currency,
    });
  };

  const trackPostedSuccesses = () => {
    if (document.querySelector('.newsletter-form__message--success')) {
      track('newsletter_signup_success');
    }

    if (
      document.querySelector(
        '.photo-contact-form__status--success, #ContactForm .form-status.form-status-list.form__message'
      )
    ) {
      track('contact_form_success');
    }
  };

  let variantEventsBound = false;

  const bindVariantEvents = (attempt = 0) => {
    if (variantEventsBound) return;

    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.variantChange) {
      if (attempt < VARIANT_BIND_RETRIES) {
        window.setTimeout(() => bindVariantEvents(attempt + 1), VARIANT_BIND_RETRY_MS);
      }

      return;
    }

    variantEventsBound = true;

    subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
      const product = document.querySelector(`[data-section="${data?.sectionId}"][data-umami-product="true"]`);
      const variant = data?.variant;
      if (!product || !variant) return;

      track('product_variant_selected', {
        product_id: product.dataset.productId,
        product_title: product.dataset.productTitle,
        product_handle: product.dataset.productHandle,
        variant_id: variant.id,
        variant_title: variant.title,
        option_1: variant.option1,
        option_2: variant.option2,
        option_3: variant.option3,
        price: centsToDecimal(variant.price),
        available: variant.available,
        sku: variant.sku,
      });
    });
  };

  document.addEventListener('click', (event) => {
    const checkoutTrigger = event.target.closest('[name="checkout"], a[href*="/checkout"]');
    if (checkoutTrigger && checkoutTrigger.getAttribute('aria-disabled') !== 'true' && !checkoutTrigger.disabled) {
      track('checkout_started', {
        source: checkoutTrigger.closest('.additional-checkout-buttons') ? 'dynamic_checkout' : 'cart_checkout',
      });
    }

    const quoteTrigger = event.target.closest('a[href*="/pages/contact?topic=photo-count"]');
    if (quoteTrigger) {
      track('quote_requested', { source: quoteTrigger.closest('product-info') ? 'product_page' : 'storefront' });
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.querySelector('[name="contact[tags]"][value*="newsletter"]')) {
      track('newsletter_signup_submitted');
      return;
    }

    if (form.matches('.photo-contact-form, form[action*="/contact"]')) {
      const topic = form.querySelector('[name="contact[Topic]"]')?.value;
      track('contact_form_submitted', { topic });
    }
  });

  window.MementoScanAnalytics = {
    track,
    trackAddToBasket,
    trackAddToBasketError,
    trackCartQuantityChange,
  };

  bindVariantEvents();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      trackInitialProductView();
      trackPostedSuccesses();
    });
  } else {
    trackInitialProductView();
    trackPostedSuccesses();
  }
})();
